import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BillingProvider, useBilling } from "./context/BillingContext";
import { BrandedSplash } from "./components/BrandedSplash";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LegalPage from "./pages/LegalPage";
import Support from "./pages/Support";
import PrivacyData from "./pages/PrivacyData";
import ConfirmEmail from "./pages/ConfirmEmail";
import Home from "./pages/Home";
import Work from "./pages/Work";
import AddWork from "./pages/AddWork";
import Practices from "./pages/Practices";
import AddPractice from "./pages/AddPractice";
import PracticeDetail from "./pages/PracticeDetail";
import EditAgreement from "./pages/EditAgreement";
import MoneyPage from "./pages/MoneyPage";
import RecordPayment from "./pages/RecordPayment";
import PaymentReportDetail from "./pages/PaymentReportDetail";
import EditPaymentReport from "./pages/EditPaymentReport";
import Expenses from "./pages/Expenses";
import AddExpense from "./pages/AddExpense";
import PaymentCheck from "./pages/PaymentCheck";
import More from "./pages/More";
import Profile from "./pages/Profile";
import Accountant from "./pages/Accountant";
import BAS from "./pages/BAS";
import Reserves from "./pages/Reserves";
import Security from "./pages/Security";
import Paywall from "./pages/Paywall";
import AccessEnded from "./pages/AccessEnded";
import BillingSuccess from "./pages/BillingSuccess";
import NavScoraIntegration from "./pages/NavScoraIntegration";
import { isNativePlatform, expiredRedirectPath } from "./lib/platform";

function Loader() {
  return (
    <div className="df-shell flex items-center justify-center min-h-screen">
      <div className="animate-pulse text-[#0D786A] font-heading font-bold text-lg">Molaroo</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function BillingGate({ children }) {
  const { billing, loading } = useBilling();
  if (loading) return <Loader />;
  if (billing && !billing.allowed) return <Navigate to={expiredRedirectPath()} replace />;
  return children;
}

function Gated({ children }) {
  return <Protected><BillingGate>{children}</BillingGate></Protected>;
}

function PremiumGate({ children }) {
  const { billing, loading } = useBilling();
  if (loading) return <Loader />;
  if (billing && !billing.premium) return <Navigate to={expiredRedirectPath()} replace />;
  return children;
}

function GatedPremium({ children }) {
  return <Protected><BillingGate><PremiumGate>{children}</PremiumGate></BillingGate></Protected>;
}

// Privacy & Data (export + account deletion) must stay reachable for an expired NATIVE user so
// they can manage/delete their account (App Store requirement). Web keeps its existing behaviour
// (billing-gated — expired web users are on the Stripe paywall, unchanged).
function AccountGate({ children }) {
  return isNativePlatform() ? <Protected>{children}</Protected> : <Gated>{children}</Gated>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BillingProvider>
          <BrowserRouter>
          <BrandedSplash />
          <Toaster position="top-center" richColors offset="72px" mobileOffset="72px" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/support" element={<Support />} />
            <Route path="/paywall" element={<Protected><Paywall /></Protected>} />
            <Route path="/access-ended" element={<Protected><AccessEnded /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/billing/cancel" element={<Navigate to="/paywall" replace />} />
            <Route path="/" element={<Gated><Home /></Gated>} />
            <Route path="/work" element={<Gated><Work /></Gated>} />
            <Route path="/work/add" element={<Gated><AddWork /></Gated>} />
            <Route path="/work/:id/edit" element={<Gated><AddWork /></Gated>} />
            <Route path="/practices" element={<Gated><Practices /></Gated>} />
            <Route path="/practices/add" element={<Gated><AddPractice /></Gated>} />
            <Route path="/practices/:id" element={<Gated><PracticeDetail /></Gated>} />
            <Route path="/practices/:id/agreement" element={<Gated><EditAgreement /></Gated>} />
            <Route path="/practices/:id/check" element={<Gated><PaymentCheck /></Gated>} />
            <Route path="/money" element={<Gated><MoneyPage /></Gated>} />
            <Route path="/money/record" element={<Gated><RecordPayment /></Gated>} />
            <Route path="/money/report/:id" element={<Gated><PaymentReportDetail /></Gated>} />
            <Route path="/money/report/:id/edit" element={<Gated><EditPaymentReport /></Gated>} />
            <Route path="/money/expenses" element={<Gated><Expenses /></Gated>} />
            <Route path="/money/expense/new" element={<Gated><AddExpense /></Gated>} />
            <Route path="/money/expense/:id" element={<Gated><AddExpense /></Gated>} />
            <Route path="/more" element={<Gated><More /></Gated>} />
            <Route path="/profile" element={<Gated><Profile /></Gated>} />
            <Route path="/more/accountant" element={<GatedPremium><Accountant /></GatedPremium>} />
            <Route path="/more/bas" element={<GatedPremium><BAS /></GatedPremium>} />
            <Route path="/more/reserves" element={<GatedPremium><Reserves /></GatedPremium>} />
            <Route path="/more/security" element={<Gated><Security /></Gated>} />
            <Route path="/more/navscora" element={<Protected><NavScoraIntegration /></Protected>} />
            <Route path="/more/privacy" element={<AccountGate><PrivacyData /></AccountGate>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </BillingProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
